import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Clock, MapPin } from 'lucide-react';

const compatibilitySchema = z.object({
  person1: z.object({
    name: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
    birthDate: z.string().min(1, 'Informe a data de nascimento'),
    birthTime: z.string().optional(),
    birthPlace: z.string().optional()
  }),
  person2: z.object({
    name: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
    birthDate: z.string().min(1, 'Informe a data de nascimento'),
    birthTime: z.string().optional(),
    birthPlace: z.string().optional()
  })
});

type CompatibilityFormData = z.infer<typeof compatibilitySchema>;

export interface CompatibilityData {
  person1: { name: string; birthDate: string; birthTime?: string; birthPlace?: string };
  person2: { name: string; birthDate: string; birthTime?: string; birthPlace?: string };
}

interface CompatibilityFormProps {
  // Página recebe os dados e roda a análise estática modelo
  onResult: (result: CompatibilityData) => void;
}

// Modelo estático: análise determinística local (IA volta quando migrar para o server)
export const CompatibilityForm: React.FC<CompatibilityFormProps> = ({ onResult }) => {
  const { register, handleSubmit, formState: { errors } } = useForm<CompatibilityFormData>({
    resolver: zodResolver(compatibilitySchema)
  });

  const onSubmit = (data: CompatibilityFormData) => {
    onResult(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pessoa 1 */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-purple-100 flex items-center">
            <User className="w-5 h-5 mr-2 text-purple-300" />
            Pessoa 1
          </h3>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">Nome</label>
            <input
              {...register('person1.name')}
              className="input-primary"
              placeholder="Nome completo"
            />
            {errors.person1?.name && (
              <p className="text-red-400 text-sm mt-1">{errors.person1.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">
              Data de Nascimento
            </label>
            <input
              {...register('person1.birthDate')}
              type="date"
              className="input-primary"
            />
            {errors.person1?.birthDate && (
              <p className="text-red-400 text-sm mt-1">{errors.person1.birthDate.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">
              <Clock className="w-4 h-4 inline mr-2" />
              Horário de Nascimento (opcional)
            </label>
            <input
              {...register('person1.birthTime')}
              type="time"
              className="input-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">
              <MapPin className="w-4 h-4 inline mr-2" />
              Local de Nascimento (opcional)
            </label>
            <input
              {...register('person1.birthPlace')}
              className="input-primary"
              placeholder="Cidade onde nasceu"
            />
          </div>
        </div>

        {/* Pessoa 2 — espelho da 1 */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-purple-100 flex items-center">
            <User className="w-5 h-5 mr-2 text-purple-300" />
            Pessoa 2
          </h3>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">Nome</label>
            <input
              {...register('person2.name')}
              className="input-primary"
              placeholder="Nome completo"
            />
            {errors.person2?.name && (
              <p className="text-red-400 text-sm mt-1">{errors.person2.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">
              Data de Nascimento
            </label>
            <input
              {...register('person2.birthDate')}
              type="date"
              className="input-primary"
            />
            {errors.person2?.birthDate && (
              <p className="text-red-400 text-sm mt-1">{errors.person2.birthDate.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">
              <Clock className="w-4 h-4 inline mr-2" />
              Horário de Nascimento (opcional)
            </label>
            <input
              {...register('person2.birthTime')}
              type="time"
              className="input-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">
              <MapPin className="w-4 h-4 inline mr-2" />
              Local de Nascimento (opcional)
            </label>
            <input
              {...register('person2.birthPlace')}
              className="input-primary"
              placeholder="Cidade onde nasceu"
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        className="btn-primary w-full flex items-center justify-center space-x-2"
      >
        <span>Analisar Compatibilidade</span>
      </button>
    </form>
  );
};